import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import type { ElementsListViewV3Props } from "./types";

export const ElementsListViewV3 = ({
  creationModeState,
}: ElementsListViewV3Props) => {
  const { t } = useTranslation();

  const { form, activeIndex, selectElement, removeElement } = creationModeState;

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLDivElement
  >({ resetDep: activeIndex });

  return (
    <form.Field name="elements" mode="array">
      {(arrayField) => {
        const elements = arrayField.state.value;
        return (
          <div
            ref={containerRef}
            className="flex flex-col gap-2 overflow-y-auto flex-1 min-h-0 p-px"
          >
            {elements.map((element, index) => (
              <div
                key={element.id}
                ref={activeIndex === index ? targetRef : undefined}
                role="button"
                tabIndex={0}
                onClick={() => selectElement(index)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    selectElement(index);
                  }
                }}
                className={clsx(
                  "rounded-lg border bg-white shrink-0 cursor-pointer focus:outline-none",
                  activeIndex === index
                    ? "border-blue-400 ring-1 ring-blue-400"
                    : "border-gray-200 hover:border-gray-300",
                )}
              >
                <div
                  className={clsx(
                    "flex items-center justify-between px-3 py-2 border-b rounded-t-lg",
                    activeIndex === index
                      ? "bg-blue-50 border-blue-200"
                      : "bg-gray-50 border-gray-200",
                  )}
                >
                  <span
                    className={clsx(
                      "text-sm font-semibold",
                      activeIndex === index ? "text-blue-700" : "text-gray-700",
                    )}
                  >
                    {t("smartSurveyor.elementsCreation.elementLabel", {
                      index: index + 1,
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeElement(index);
                    }}
                    aria-label={t(
                      "smartSurveyor.elementsCreation.removeElement",
                    )}
                    className="text-sm px-3 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors"
                  >
                    {t("common.delete")}
                  </button>
                </div>
                <div className="p-3 space-y-3">
                  <form.AppField name={`elements[${index}].multiplier`}>
                    {(field) => (
                      <field.NumberField
                        label={t(
                          "smartSurveyor.elementDetails.form.multiplier",
                        )}
                        isEditable
                        step={1}
                        min={1}
                        inputMode="numeric"
                      />
                    )}
                  </form.AppField>
                  <div className="text-sm">
                    <div className="text-sm font-medium text-gray-700 mb-1">
                      {t("smartSurveyor.elementDetails.form.elementType")}
                    </div>
                    <div className="text-gray-900">{element.group_id}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      }}
    </form.Field>
  );
};
